import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const imgSrc = (image) => (image?.startsWith("http") ? image : `${API}/files/${image}`);

const ProductCard = ({ product, index }) => {
  const { addItem } = useCart();
  return (
    <motion.article
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/producto/${product.id}`} data-testid={`product-link-${product.id}`} className="block relative overflow-hidden aspect-[3/4] bg-noir-soft border border-white/5">
        <img
          src={imgSrc(product.image)}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addItem(product);
            toast.success(`${product.name} añadido a la cesta`);
          }}
          className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 bg-gold text-black text-[11px] uppercase tracking-[0.25em] py-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out hover:bg-gold-light"
        >
          <Plus size={14} /> Añadir a la cesta
        </button>
      </Link>
      <div className="pt-4 sm:pt-6 text-center">
        <Link to={`/producto/${product.id}`}>
          <h3 className="font-serif-lux text-lg sm:text-2xl tracking-[0.12em] sm:tracking-[0.15em] uppercase text-white hover:text-gold transition-colors duration-300">{product.name}</h3>
        </Link>
        <p className="text-neutral-500 text-xs sm:text-sm font-light mt-1.5 sm:mt-2 line-clamp-2 max-w-xs mx-auto">{product.description}</p>
        <p className="text-gold font-serif-lux text-base sm:text-xl mt-2 sm:mt-3 tracking-wider" data-testid={`product-price-${product.id}`}>
          {product.price.toFixed(2)} €
        </p>
      </div>
    </motion.article>
  );
};

export const Catalog = ({ products }) => {
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category");

  const filteredProducts = products.filter((p) => {
    if (!categoryFilter) return true;
    const cat = (p.category || "unisex").toLowerCase();
    return cat === categoryFilter || cat === "unisex";
  });

  return (
    <section id="coleccion" className="relative py-32 px-6" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="text-center mb-20"
        >
          <span className="font-script-lux text-gold text-3xl">La colección</span>
          <h2 className="font-serif-lux text-4xl sm:text-5xl tracking-[0.2em] uppercase text-white mt-4">
            {categoryFilter === "hombre" ? "Colección Hombre" : categoryFilter === "mujer" ? "Colección Mujer" : "Perfumes de excepción"}
          </h2>
          <div className="w-16 h-px bg-gold mx-auto mt-8" />
        </motion.div>

        {filteredProducts.length === 0 ? (
          <p className="text-center text-neutral-500 font-light" data-testid="catalog-empty">La colección llegará muy pronto.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-12 lg:gap-16">
            {filteredProducts.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
